// Job workspace. While extracting: a progress screen you can freely leave —
// the pipeline runs server-side and this page just polls, so coming back
// later lands on the same state. Once ready: sectioned review tabs on the
// left, the live formatted preview on the right, updating on every keystroke
// (spec FR-030/031 reinterpreted per the July-2026 mocks — the source stays
// one click away via "Open source").

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  AlignLeft,
  ArrowLeft,
  Redo2,
  Undo2,
  Award,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  ExternalLink,
  FileDown,
  FileText,
  FolderOpen,
  GraduationCap,
  Minus,
  Plus,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
  User,
  UserRound,
  Users,
  Wrench,
  X,
} from "lucide-react";
import type {
  AgpResume,
  GrammarFix,
  SectionKey,
  ParsedPayload,
  SourcePageInfo,
  SourceRedactions,
  SourceTextDoc,
  SourceTextEdits,
  Consultant,
  FooterPreset,
  OutputInfo,
  ResumeJobStatus,
  ResumeSourceInfo,
  TemplateInfo,
} from "@agp/shared";
import { resolveFooterLines, withFooterLines } from "@agp/shared";
import { useApi } from "@/hooks/useApi";
import { STATUS_LABEL } from "@/lib/jobs";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input, Label, Select } from "@/components/ui/input";
import {
  CertificationsEditor,
  ContentLinesEditor,
  EducationEditor,
  ExperienceEditor,
  LanguagesEditor,
  OtherSectionsEditor,
  SectionCard,
  StringListEditor,
  isLowConfidence,
} from "@/components/review/editors";
import { TemplatePreview, type JumpTarget } from "@/components/templates/TemplatePreview";
import { TabRail, TAB_SECTIONS, tabOrder, type TabKey } from "@/components/review/TabRail";
import type { AppendixSource } from "@/components/preview/AppendixPages";
import { useResumeHistory } from "@/hooks/useResumeHistory";

interface JobDetailData {
  id: string;
  status: ResumeJobStatus;
  sourceFilename: string;
  sourceMime: string;
  warnings: string[];
  error: string | null;
  photoUrl: string | null;
}

// ---- extraction progress ----------------------------------------------------

function ExtractingView({ filename }: { filename: string }) {
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col items-center gap-6 py-14">
          <div className="relative">
            <div className="flex h-40 w-40 items-center justify-center rounded-full bg-brand-tint">
              <div className="relative h-24 w-[76px] overflow-hidden rounded-md bg-white shadow-raised">
                <div className="absolute inset-x-3 top-4 h-1.5 rounded-full bg-brand-400" />
                <div className="absolute inset-x-3 top-8 h-1.5 rounded-full bg-brand-200" />
                <div className="absolute inset-x-3 top-12 h-1.5 w-2/3 rounded-full bg-brand-200" />
                <div className="resume-scan-beam absolute inset-x-0 h-10" />
              </div>
            </div>
            {/* Decorative dots per the mock — hidden from readers. */}
            <span aria-hidden className="absolute -left-3 top-8 h-2 w-2 rounded-full bg-success" />
            <span aria-hidden className="absolute -right-4 top-14 h-2.5 w-2.5 rounded-full bg-brand-400" />
            <span aria-hidden className="absolute -bottom-1 left-6 h-2 w-2 rounded-full bg-purple-on" />
            <span aria-hidden className="absolute right-2 -top-2 h-2 w-2 rounded-full bg-success" />
          </div>
          <div className="max-w-xl text-center">
            <h3>Reading {filename}…</h3>
            <p className="mt-2 text-[14px] text-text-2">
              Extracting text, mapping sections and scoring confidence. This usually takes under
              30 seconds — you can move to other pages and come back any time.
            </p>
          </div>
          <div className="h-1.5 w-72 overflow-hidden rounded-full bg-surface-3">
            <div className="resume-indeterminate h-full w-1/3 rounded-full bg-brand-500" />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-wrap items-center gap-4 py-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-tint text-brand-on">
            <Sparkles size={19} strokeWidth={1.75} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-text">What happens next?</p>
            <p className="mt-0.5 text-[13px] text-text-2">
              We&rsquo;ll extract key details from the document, then let you review and refine
              them before generating a formatted resume.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// ---- main page --------------------------------------------------------------

export function JobDetail() {
  const { id = "" } = useParams();
  const api = useApi();
  const navigate = useNavigate();

  const jobQuery = useQuery({
    queryKey: ["job", id],
    queryFn: () => api.get<JobDetailData>(`/api/resume-jobs/${id}`),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "extracting" || s === "created" ? 1500 : false;
    },
  });
  const status = jobQuery.data?.status;

  const parsedQuery = useQuery({
    queryKey: ["job-parsed", id],
    queryFn: () =>
      api.get<ParsedPayload>(`/api/resume-jobs/${id}/parsed`),
    enabled: status === "ready_for_review",
    staleTime: Infinity,
  });

  // The page owns one resume object; the form's editors and the preview's
  // in-place editing both replace slices of it, so each side always shows
  // the other's edits. Autosave PATCHes a revision after edits settle
  // (spec FR-036); undo/redo walks whole snapshots.
  const history = useResumeHistory(null);
  const resume = history.resume;
  const [warnings, setWarnings] = useState<string[]>([]);
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  const [grammarFixes, setGrammarFixes] = useState<GrammarFix[]>([]);
  const [grammarOpen, setGrammarOpen] = useState(false);
  const [grammarState, setGrammarState] = useState<"idle" | "busy" | "error">("idle");
  const [saveState, setSaveState] = useState<"idle" | "dirty" | "saving" | "saved" | "error">("idle");
  const [activeTab, setActiveTab] = useState<TabKey>("candidate");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestResume = useRef<AgpResume | null>(null);

  useEffect(() => {
    if (parsedQuery.data && resume === null) {
      const initial = parsedQuery.data.edited ?? parsedQuery.data.extracted;
      history.reset(initial);
      latestResume.current = initial;
      setWarnings(parsedQuery.data.warnings);
      setGrammarFixes(parsedQuery.data.grammarFixes ?? []);
    }
  }, [parsedQuery.data, resume, history]);

  const persist = useCallback(async () => {
    if (!latestResume.current) return;
    setSaveState("saving");
    try {
      const res = await api.patch<{ revisionId: string; warnings: string[] }>(
        `/api/resume-jobs/${id}/parsed`,
        { data: latestResume.current },
      );
      setWarnings(res.warnings);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [api, id]);

  const scheduleSave = useCallback(() => {
    setSaveState("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void persist(), 1200);
  }, [persist]);

  /** Replaces the whole resume (the preview edits this way). `key` groups a
   *  burst of keystrokes into one undo step. */
  const commitResume = useCallback(
    (next: AgpResume, key: string | null) => {
      latestResume.current = next;
      history.apply(next, key);
      scheduleSave();
    },
    [history, scheduleSave],
  );

  const updateResume = useCallback(
    (patch: Partial<AgpResume>) => {
      const prev = latestResume.current;
      if (!prev) return;
      commitResume({ ...prev, ...patch }, Object.keys(patch).join(","));
    },
    [commitResume],
  );

  const undo = useCallback(() => {
    const prev = history.undo();
    if (prev) {
      latestResume.current = prev;
      scheduleSave();
    }
  }, [history, scheduleSave]);
  const redo = useCallback(() => {
    const next = history.redo();
    if (next) {
      latestResume.current = next;
      scheduleSave();
    }
  }, [history, scheduleSave]);

  // Spelling and grammar over the current text; the result replaces the
  // resume as one undo step, so it can be taken back with ⌘Z.
  const runGrammar = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      await persist();
    }
    setGrammarState("busy");
    try {
      const res = await api.post<{ data: AgpResume; fixes: GrammarFix[] }>(`/api/resume-jobs/${id}/grammar`, {});
      setGrammarFixes(res.fixes);
      setGrammarOpen(true);
      if (res.fixes.length > 0) {
        latestResume.current = res.data;
        history.apply(res.data, null);
        setSaveState("saved");
      }
      setGrammarState("idle");
    } catch {
      setGrammarState("error");
    }
  }, [api, id, history, persist]);

  // Something only the form edits (dates) was clicked in the preview: show
  // that entry's form.
  const jumpTo = useCallback((target: JumpTarget) => {
    setActiveTab(target.tab);
    if (target.tab === "candidate") return;
    window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-entry="${target.tab}-${target.index}"]`);
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.querySelector<HTMLElement>("select, input")?.focus({ preventScroll: true });
    }, 50);
  }, []);

  // Flush pending edits when leaving the page.
  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        void persist();
      }
    },
    [persist],
  );

  // Password-protected sources: the extract endpoint accepts a password,
  // decrypts server-side and re-runs the pipeline.
  const [password, setPassword] = useState("");
  const [unlockState, setUnlockState] = useState<"idle" | "busy" | "error">("idle");
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const unlock = useCallback(async () => {
    if (!password) return;
    setUnlockState("busy");
    setUnlockError(null);
    try {
      await api.post(`/api/resume-jobs/${id}/extract`, { password });
      setPassword("");
      setUnlockState("idle");
      await jobQuery.refetch();
    } catch (err) {
      setUnlockState("error");
      setUnlockError(err instanceof Error ? err.message : "Could not unlock the file.");
    }
  }, [api, id, password, jobQuery]);

  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardBusy, setDiscardBusy] = useState(false);
  const deleteJob = useCallback(async () => {
    setDiscardBusy(true);
    try {
      await api.delete(`/api/resume-jobs/${id}`);
      navigate("/");
    } finally {
      setDiscardBusy(false);
    }
  }, [api, id, navigate]);

  // ---- templates, generation and the live preview ---------------------------

  // No staleTime: refetch on mount/focus so a template published moments ago
  // shows up here without a hard refresh (the builder also invalidates this
  // key on publish).
  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<{ templates: TemplateInfo[]; pdfAvailable: boolean }>("/api/templates"),
    enabled: status === "ready_for_review",
  });
  // Resumes render through resume templates only (JD templates have their
  // own review screen).
  const templates = (templatesQuery.data?.templates ?? []).filter(
    (t) => t.definition.kind !== "jd",
  );
  const pdfAvailable = templatesQuery.data?.pdfAvailable ?? false;
  // App-wide consultant list (admins maintain it on the Settings page).
  const consultantsQuery = useQuery({
    queryKey: ["consultants"],
    queryFn: () => api.get<{ consultants: Consultant[] }>("/api/consultants"),
    enabled: status === "ready_for_review",
  });
  const consultants = consultantsQuery.data?.consultants ?? [];
  const footerPresetsQuery = useQuery({
    queryKey: ["footer-presets"],
    queryFn: () => api.get<{ presets: FooterPreset[] }>("/api/footer-presets"),
    enabled: status === "ready_for_review",
  });
  const footerPresets = footerPresetsQuery.data?.presets ?? [];
  const [templateId, setTemplateId] = useState("");
  // null follows the template's default footer; a name overrides it.
  const [footerChoice, setFooterChoice] = useState<string | null>(null);
  const [includeContact, setIncludeContact] = useState(false);
  const [zoom, setZoom] = useState(1);
  const selectedTemplate = templates.find((t) => t.id === templateId) ?? templates[0] ?? null;
  const previewDefinition = useMemo(
    () =>
      selectedTemplate
        ? withFooterLines(
            selectedTemplate.definition,
            resolveFooterLines(selectedTemplate.definition, footerPresets, footerChoice),
          )
        : null,
    [selectedTemplate, footerPresets, footerChoice],
  );

  const sourcesQuery = useQuery({
    queryKey: ["job-sources", id],
    queryFn: () => api.get<{ sources: ResumeSourceInfo[] }>(`/api/resume-jobs/${id}/sources`),
    enabled: status === "ready_for_review",
    staleTime: 10 * 60 * 1000,
  });
  const sources = sourcesQuery.data?.sources ?? [];
  const setAppend = async (source: ResumeSourceInfo, appendToOutput: boolean) => {
    await api.patch(`/api/resume-jobs/${id}/sources/${source.id}`, { appendToOutput });
    await sourcesQuery.refetch();
  };

  // Appended originals show as real pages after the report. Redactions are
  // kept here so drawing a box feels instant, and saved after a short pause.
  const appended = sources.filter((s) => s.appendToOutput && s.canAppend);
  // Word originals open as editable text; PDFs as pages. Pages of a Word
  // original are rendered from the edited file, so they are only fetched
  // while that view is showing and re-fetched when the edits change.
  const [views, setViews] = useState<Record<string, "pages" | "text">>({});
  const viewOf = (s: ResumeSourceInfo) => views[s.id] ?? (s.editableText ? "text" : "pages");
  const [textEdits, setTextEdits] = useState<Record<string, SourceTextEdits>>({});
  // Source edits still waiting for their save timer, so generate can send
  // them first instead of racing the timer.
  const pendingSourcePatches = useRef<Record<string, { textEdits?: SourceTextEdits; redactions?: SourceRedactions }>>({});
  const sourceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const flushSourcePatch = useCallback(
    async (sourceId: string) => {
      clearTimeout(sourceTimers.current[sourceId]);
      const patch = pendingSourcePatches.current[sourceId];
      if (!patch) return;
      delete pendingSourcePatches.current[sourceId];
      await api.patch(`/api/resume-jobs/${id}/sources/${sourceId}`, patch);
    },
    [api, id],
  );
  const saveSourcePatch = useCallback(
    (sourceId: string, patch: { textEdits?: SourceTextEdits; redactions?: SourceRedactions }) => {
      pendingSourcePatches.current[sourceId] = { ...pendingSourcePatches.current[sourceId], ...patch };
      clearTimeout(sourceTimers.current[sourceId]);
      sourceTimers.current[sourceId] = setTimeout(() => void flushSourcePatch(sourceId), 600);
    },
    [flushSourcePatch],
  );
  const flushSourcePatches = useCallback(
    () => Promise.all(Object.keys(pendingSourcePatches.current).map(flushSourcePatch)),
    [flushSourcePatch],
  );
  const textQueries = useQueries({
    queries: appended.map((s) => ({
      queryKey: ["source-text", id, s.id],
      queryFn: () =>
        api.get<SourceTextDoc & { textEdits: SourceTextEdits }>(`/api/resume-jobs/${id}/sources/${s.id}/text`),
      enabled: s.editableText,
      staleTime: Infinity,
      retry: 0,
    })),
  });
  const editsOf = (s: ResumeSourceInfo, i: number) => textEdits[s.id] ?? textQueries[i]?.data?.textEdits ?? s.textEdits;
  const pageQueries = useQueries({
    queries: appended.map((s, i) => ({
      queryKey: ["source-pages", id, s.id, s.editableText ? JSON.stringify(editsOf(s, i)) : ""],
      queryFn: () =>
        api.get<{ pages: SourcePageInfo[]; redactions: SourceRedactions }>(
          `/api/resume-jobs/${id}/sources/${s.id}/pages`,
        ),
      enabled: viewOf(s) === "pages",
      staleTime: Infinity,
      retry: 0,
    })),
  });
  const setSourceTextEdits = useCallback(
    (sourceId: string, next: SourceTextEdits) => {
      setTextEdits((prev) => ({ ...prev, [sourceId]: next }));
      saveSourcePatch(sourceId, { textEdits: next });
    },
    [saveSourcePatch],
  );
  const [redactions, setRedactions] = useState<Record<string, SourceRedactions>>({});
  const setSourceRedactions = useCallback(
    (sourceId: string, next: SourceRedactions) => {
      setRedactions((prev) => ({ ...prev, [sourceId]: next }));
      saveSourcePatch(sourceId, { redactions: next });
    },
    [saveSourcePatch],
  );
  const appendix: AppendixSource[] = appended.map((s, i) => ({
    id: s.id,
    filename: s.filename,
    pages: pageQueries[i]?.data?.pages ?? null,
    redactions: redactions[s.id] ?? pageQueries[i]?.data?.redactions ?? s.redactions,
    onChange: (next) => setSourceRedactions(s.id, next),
    view: viewOf(s),
    text: s.editableText
      ? {
          doc: textQueries[i]?.data ?? null,
          textEdits: editsOf(s, i),
          onChange: (next) => setSourceTextEdits(s.id, next),
        }
      : undefined,
  }));

  const [generating, setGenerating] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const genRef = useRef<HTMLDivElement | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!genOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!genRef.current?.contains(e.target as Node)) setGenOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setGenOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [genOpen]);

  // Generate the chosen format, then start the download immediately — the
  // stored output (audit, reports) and the user's copy come from one action.
  const generate = useCallback(
    async (format: "docx" | "pdf") => {
      if (!selectedTemplate) return;
      setGenOpen(false);
      setGenerating(true);
      setActionError(null);
      try {
        await Promise.all([persist(), flushSourcePatches()]);
        const res = await api.post<{ outputs: Array<{ id: string; format: "docx" | "pdf" }> }>(
          `/api/resume-jobs/${id}/generate`,
          {
            templateId: selectedTemplate.id,
            includeContact,
            formats: [format],
            footerPreset: footerChoice,
          },
        );
        // The generate response has ids only; the outputs list carries the
        // fresh presigned link (Content-Disposition names the file).
        const outs = await api.get<{ outputs: OutputInfo[] }>(`/api/resume-jobs/${id}/outputs`);
        const target = outs.outputs.find((o) => o.id === res.outputs[0]?.id);
        if (target) {
          try {
            // Pull the bytes and save via a blob URL. If the storage host
            // blocks cross-origin fetch (real S3 without a CORS rule), fall
            // back to plain navigation — the link's Content-Disposition
            // makes that a download too.
            const fileRes = await fetch(target.downloadUrl);
            if (!fileRes.ok) throw new Error(`download failed (${fileRes.status})`);
            const blobUrl = URL.createObjectURL(await fileRes.blob());
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = target.filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
          } catch {
            window.location.assign(target.downloadUrl);
          }
        }
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Generation failed.");
      } finally {
        setGenerating(false);
      }
    },
    [api, id, selectedTemplate, includeContact, footerChoice, persist, flushSourcePatches],
  );

  const candidate = resume?.candidate ?? null;
  const saveLabel = useMemo(() => {
    switch (saveState) {
      case "dirty":
      case "saving":
        return "Saving…";
      case "saved":
        return "All changes saved";
      case "error":
        return "Save failed — edits retry on next change";
      default:
        return "";
    }
  }, [saveState]);

  if (jobQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-4 w-36" />
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-64" />
          </div>
        </div>
        <Skeleton className="h-72 w-full rounded-lg" />
      </div>
    );
  }
  if (!jobQuery.data) {
    return (
      <EmptyState
        icon={<FileText className="h-5 w-5" strokeWidth={1.75} aria-hidden />}
        title="Resume not found"
        body="It may have been deleted, or the link is stale."
        action={
          <Button asChild variant="secondary">
            <Link to="/resumes">Back to resumes</Link>
          </Button>
        }
      />
    );
  }
  const job = jobQuery.data;
  const statusMeta = STATUS_LABEL[job.status];
  const extracting = job.status === "extracting" || job.status === "created";
  const ready = job.status === "ready_for_review" && resume !== null && candidate !== null;
  const title = candidate?.fullName ?? job.sourceFilename;

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/resumes"
        className="inline-flex items-center gap-1.5 self-start text-[13.5px] font-semibold text-brand-on no-underline hover:underline"
      >
        <ArrowLeft size={15} strokeWidth={2} aria-hidden />
        Back to resumes
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar src={job.photoUrl} alt={title} size="md" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="truncate">{title}</h3>
              <Badge variant={statusMeta.variant}>
                {job.status === "ready_for_review" && (
                  <Check size={12} strokeWidth={2.5} aria-hidden />
                )}
                {statusMeta.label}
              </Badge>
            </div>
            <p className="truncate text-[12px] text-text-2">
              {job.sourceFilename}
              {saveLabel && ` · ${saveLabel}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDiscardOpen(true)}>
            <Trash2 size={14} strokeWidth={1.75} aria-hidden />
            {ready ? "Discard" : "Delete"}
          </Button>
          {ready && (
            <div ref={genRef} className="relative">
              <Button
                size="sm"
                disabled={generating || !selectedTemplate}
                aria-haspopup="true"
                aria-expanded={genOpen}
                onClick={() => setGenOpen((v) => !v)}
              >
                <Sparkles size={14} strokeWidth={1.75} aria-hidden />
                {generating ? "Generating…" : "Generate document"}
                <ChevronDown size={13} strokeWidth={2} aria-hidden />
              </Button>
              {genOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-40 mt-1 w-60 animate-fade-in overflow-hidden rounded-md border border-border bg-surface py-1.5 shadow-pop"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void generate("docx")}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-text hover:bg-surface-2"
                  >
                    <FileText className="h-4 w-4 text-brand-on" strokeWidth={1.75} aria-hidden />
                    Word document (.docx)
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!pdfAvailable}
                    onClick={() => void generate("pdf")}
                    className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-text hover:bg-surface-2 disabled:pointer-events-none disabled:opacity-50"
                  >
                    <FileDown className="h-4 w-4 text-danger-on" strokeWidth={1.75} aria-hidden />
                    PDF
                    {!pdfAvailable && (
                      <span className="ml-auto text-[11px] text-text-3">unavailable</span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title={ready ? "Discard this resume?" : "Delete this resume?"}
        body="The uploaded file and every generated document for it are removed. This cannot be undone."
        confirmLabel={ready ? "Discard" : "Delete"}
        danger
        busy={discardBusy}
        onConfirm={() => void deleteJob()}
        onCancel={() => setDiscardOpen(false)}
      />

      {actionError && (
        <p className="rounded-md bg-danger-tint px-4 py-2.5 text-[13px] font-medium text-danger-on">
          {actionError}
        </p>
      )}

      {extracting && <ExtractingView filename={job.sourceFilename} />}

      {job.status === "failed" &&
        (/password/i.test(job.error ?? "") ? (
          <Card className="border-warn">
            <CardBody className="flex flex-col gap-3">
              <div>
                <p className="font-semibold text-text">This file is password protected</p>
                <p className="mt-1 text-[14px] text-text-2">
                  Enter the document password to unlock and extract it. The password is used
                  once and never stored.
                </p>
              </div>
              <form
                className="flex max-w-md items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void unlock();
                }}
              >
                <Input
                  type="password"
                  value={password}
                  placeholder="Document password"
                  autoComplete="off"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button type="submit" disabled={!password || unlockState === "busy"}>
                  {unlockState === "busy" ? "Unlocking…" : "Unlock & extract"}
                </Button>
              </form>
              {unlockError && (
                <p className="rounded-md bg-danger-tint px-3 py-2 text-[13px] font-medium text-danger-on">
                  {unlockError}
                </p>
              )}
            </CardBody>
          </Card>
        ) : (
          <Card className="border-danger">
            <CardBody>
              <p className="font-semibold text-danger-on">Extraction failed</p>
              <p className="mt-1 text-[14px] text-text-2">{job.error}</p>
              <Button className="mt-3" variant="secondary" onClick={() => navigate("/")}>
                Back to upload
              </Button>
            </CardBody>
          </Card>
        ))}

      {ready && (
        <>
          {warnings.length > 0 && !warningsDismissed && (
            <div className="flex items-start gap-3 rounded-lg border border-warn bg-warn-tint px-4 py-3">
              <TriangleAlert
                size={17}
                strokeWidth={1.75}
                aria-hidden
                className="mt-0.5 shrink-0 text-warn-on"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-bold text-warn-on">Check before generating</p>
                <ul className="mt-1 list-disc pl-5 text-[13px] leading-relaxed text-warn-on">
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                aria-label="Dismiss warnings"
                onClick={() => setWarningsDismissed(true)}
                className="shrink-0 rounded-md p-1 text-warn-on hover:bg-warn-tint"
              >
                <X size={15} strokeWidth={2} aria-hidden />
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-surface px-4 py-2.5">
            <div className="min-w-0">
              <p className="text-[13.5px] font-semibold text-text">
                Spelling &amp; grammar
                {grammarFixes.length > 0 ? ` · ${grammarFixes.length} line${grammarFixes.length === 1 ? "" : "s"} corrected` : " · nothing to fix"}
              </p>
              <p className="text-[12px] text-text-2">
                Only spelling, grammar and first-person pronouns are changed; the candidate's words stay.
                {grammarState === "error" && " The check could not run just now."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {grammarFixes.length > 0 && (
                <Button variant="secondary" size="sm" onClick={() => setGrammarOpen((o) => !o)}>
                  {grammarOpen ? "Hide changes" : "Show changes"}
                </Button>
              )}
              <Button variant="secondary" size="sm" disabled={grammarState === "busy"} onClick={() => void runGrammar()}>
                {grammarState === "busy" ? "Checking…" : "Check again"}
              </Button>
            </div>
            {grammarOpen && grammarFixes.length > 0 && (
              <ul className="mt-1 w-full divide-y divide-border text-[13px]" data-testid="grammar-fixes">
                {grammarFixes.map((f, i) => (
                  <li key={i} className="grid gap-x-4 py-1.5 sm:grid-cols-2">
                    <span className="text-text-2 line-through decoration-text-3">{f.before}</span>
                    <span className="text-text">{f.after}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* One card holds the tab strip AND the editors (tabs merged with
              the inputs), and stretches to the preview column's height. The
              preview column keeps self-start so it can stay sticky when the
              editor side is the taller one. */}
          <div className="grid gap-4 xl:grid-cols-[minmax(0,10fr)_minmax(0,11fr)]">
            {/* The editor card is viewport-capped with its OWN scrollbar (the
                tab strip stays put, only the inputs scroll) — long tabs like
                Experience must not grow the page itself. min-h-0 on the body
                is what lets a flex child actually shrink and scroll. */}
            <Card className="flex min-w-0 flex-col xl:max-h-[calc(100vh-2rem)]">
              <TabRail
                active={activeTab}
                onSelect={setActiveTab}
                order={tabOrder(resume?.sectionOrder)}
                onReorder={(keys) => updateResume({ sectionOrder: keys.flatMap((k) => TAB_SECTIONS[k]) })}
                attention={{
                  candidate: isLowConfidence(candidate!.confidence),
                  other: resume!.unmappedContent.length > 0,
                }}
              />
              <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 xl:overflow-y-auto">
              {activeTab === "candidate" && (
                <SectionCard title="Client & position">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Client</Label>
                      <Input
                        value={resume!.candidateReport?.client ?? ""}
                        onChange={(e) =>
                          updateResume({
                            candidateReport: {
                              client: e.target.value || null,
                              position: resume!.candidateReport?.position ?? null,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Position</Label>
                      <Input
                        value={resume!.candidateReport?.position ?? ""}
                        onChange={(e) =>
                          updateResume({
                            candidateReport: {
                              client: resume!.candidateReport?.client ?? null,
                              position: e.target.value || null,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <p className="text-[12px] text-text-3">
                    Opens the output when the template includes the Candidate Report section.
                    Fields left empty render as blank lines for the consultant to complete in
                    Word.
                  </p>
                </SectionCard>
              )}

              {activeTab === "candidate" && (
                <SectionCard
                  title="Candidate information"
                  lowConfidence={isLowConfidence(candidate!.confidence)}
                  actions={
                    <label className="inline-flex cursor-pointer items-center gap-1.5 text-[13px] font-semibold text-brand-on">
                      <Upload size={14} aria-hidden /> {job.photoUrl ? "Replace photo" : "Upload photo"}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            void api
                              .postBinary(
                                `/api/resume-jobs/${id}/photo`,
                                file,
                                file.type || "application/octet-stream",
                              )
                              .then(() => jobQuery.refetch());
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                  }
                >
                  <div className="flex flex-wrap-reverse items-start gap-4">
                    <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Label>Full name</Label>
                        <Input
                          value={candidate!.fullName ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, fullName: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={candidate!.location ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, location: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Nationality</Label>
                        <Input
                          value={candidate!.nationality ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, nationality: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          value={candidate!.email ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, email: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Telephone</Label>
                        <Input
                          value={candidate!.phone ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, phone: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Reference ID</Label>
                        <Input
                          placeholder="AGP reference — typed here, never extracted"
                          value={candidate!.referenceId ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, referenceId: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Citizenship</Label>
                        <Input
                          value={candidate!.citizenship ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, citizenship: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Date of birth</Label>
                        <Input
                          value={candidate!.dateOfBirth ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, dateOfBirth: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Annual salary</Label>
                        <Input
                          value={candidate!.annualSalary ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, annualSalary: e.target.value || null },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Availability</Label>
                        <Input
                          value={candidate!.availability ?? ""}
                          onChange={(e) =>
                            updateResume({
                              candidate: { ...candidate!, availability: e.target.value || null },
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1.5">
                      {job.photoUrl ? (
                        <img
                          src={job.photoUrl}
                          alt="Candidate"
                          className="h-28 w-24 rounded-md border border-border object-cover"
                        />
                      ) : (
                        <span className="flex h-28 w-24 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border-2 px-2 text-center text-text-3">
                          <User size={20} strokeWidth={1.75} aria-hidden />
                          <span className="text-[10px] leading-tight">JPG, PNG or WebP</span>
                        </span>
                      )}
                      {job.photoUrl && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-[12px] font-semibold text-danger-on"
                          onClick={() =>
                            void api
                              .delete(`/api/resume-jobs/${id}/photo`)
                              .then(() => jobQuery.refetch())
                          }
                        >
                          <Trash2 size={12} aria-hidden /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Languages</Label>
                    <LanguagesEditor
                      items={candidate!.languages}
                      onChange={(languages) =>
                        updateResume({ candidate: { ...candidate!, languages } })
                      }
                    />
                  </div>
                  <p className="text-[12px] text-text-3">
                    Word and PDF sources fill the photo automatically when one is found; it
                    appears in the output only when the template enables photos. The candidate
                    owner is assigned from the resume list.
                  </p>
                </SectionCard>
              )}

              {activeTab === "consultant" && (
                <SectionCard title="Consultant summary">
                  {consultants.length > 0 && (
                    <div>
                      <Label>Fill from consultant</Label>
                      <Select
                        value=""
                        aria-label="Fill from consultant"
                        onChange={(e) => {
                          const con = consultants[Number(e.target.value)];
                          if (!con) return;
                          updateResume({
                            consultant: {
                              preparedBy: con.name || null,
                              telephone: con.telephone || null,
                              email: con.email || null,
                              // The date is deliberately left as-is: the
                              // feedback wants the dropdown to fill only
                              // Prepared by / Telephone / Email.
                              date: resume!.consultant?.date ?? null,
                            },
                          });
                        }}
                      >
                        <option value="">Choose a consultant…</option>
                        {consultants.map((con, i) => (
                          <option key={i} value={i}>
                            {con.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Prepared by</Label>
                      <Input
                        value={resume!.consultant?.preparedBy ?? ""}
                        onChange={(e) =>
                          updateResume({
                            consultant: {
                              preparedBy: e.target.value || null,
                              telephone: resume!.consultant?.telephone ?? null,
                              email: resume!.consultant?.email ?? null,
                              date: resume!.consultant?.date ?? null,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Telephone</Label>
                      <Input
                        value={resume!.consultant?.telephone ?? ""}
                        onChange={(e) =>
                          updateResume({
                            consultant: {
                              preparedBy: resume!.consultant?.preparedBy ?? null,
                              telephone: e.target.value || null,
                              email: resume!.consultant?.email ?? null,
                              date: resume!.consultant?.date ?? null,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        value={resume!.consultant?.email ?? ""}
                        onChange={(e) =>
                          updateResume({
                            consultant: {
                              preparedBy: resume!.consultant?.preparedBy ?? null,
                              telephone: resume!.consultant?.telephone ?? null,
                              email: e.target.value || null,
                              date: resume!.consultant?.date ?? null,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input
                        placeholder="e.g. 5 August 2026"
                        value={resume!.consultant?.date ?? ""}
                        onChange={(e) =>
                          updateResume({
                            consultant: {
                              preparedBy: resume!.consultant?.preparedBy ?? null,
                              telephone: resume!.consultant?.telephone ?? null,
                              email: resume!.consultant?.email ?? null,
                              date: e.target.value || null,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <p className="text-[12px] text-text-3">
                    Shows in the output when the template includes the Consultant Summary
                    section. Fields left empty render as blank lines for the consultant to
                    complete in Word.
                  </p>
                </SectionCard>
              )}

              {activeTab === "summary" && (
                <SectionCard title="Summary">
                  <ContentLinesEditor
                    items={candidate!.summaryBullets}
                    onChange={(summaryBullets) =>
                      updateResume({ candidate: { ...candidate!, summaryBullets } })
                    }
                  />
                </SectionCard>
              )}

              {activeTab === "experience" && (
                <SectionCard title="Professional experience">
                  <ExperienceEditor
                    items={resume!.experience}
                    onChange={(experience) => updateResume({ experience })}
                  />
                </SectionCard>
              )}

              {activeTab === "education" && (
                <SectionCard title="Education">
                  <EducationEditor
                    items={resume!.education}
                    onChange={(education) => updateResume({ education })}
                  />
                </SectionCard>
              )}

              {activeTab === "skills" && (
                <>
                  <SectionCard title="Core competencies">
                    <StringListEditor
                      items={resume!.coreCompetencies}
                      onChange={(coreCompetencies) => updateResume({ coreCompetencies })}
                    />
                  </SectionCard>
                  <SectionCard title="Technical skills">
                    <StringListEditor
                      items={resume!.technicalSkills}
                      onChange={(technicalSkills) => updateResume({ technicalSkills })}
                    />
                  </SectionCard>
                </>
              )}

              {activeTab === "certifications" && (
                <>
                  <SectionCard title="Certifications">
                    <CertificationsEditor
                      items={resume!.certifications}
                      onChange={(certifications) => updateResume({ certifications })}
                    />
                  </SectionCard>
                  <SectionCard title="Publications">
                    <StringListEditor
                      items={resume!.publications}
                      onChange={(publications) => updateResume({ publications })}
                      multiline
                    />
                  </SectionCard>
                </>
              )}

              {activeTab === "other" && (
                <>
                  <SectionCard title="Footer">
                    {footerPresets.length > 0 ? (
                      <div>
                        <Label>Footer text</Label>
                        <Select
                          value={footerChoice ?? ""}
                          onChange={(e) => setFooterChoice(e.target.value || null)}
                          aria-label="Footer text"
                        >
                          <option value="">
                            Template default
                            {selectedTemplate?.definition.branding.footerPreset
                              ? ` (${selectedTemplate.definition.branding.footerPreset})`
                              : ""}
                          </option>
                          {footerPresets.map((p) => (
                            <option key={p.name} value={p.name}>
                              {p.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : (
                      <p className="text-[13px] text-text-2">
                        The template&rsquo;s own footer is used. Admins can add footer texts under
                        Settings to make them selectable here.
                      </p>
                    )}
                  </SectionCard>
                  <SectionCard title="Career interests">
                    <StringListEditor
                      items={resume!.careerInterests}
                      onChange={(careerInterests) => updateResume({ careerInterests })}
                    />
                  </SectionCard>
                  <SectionCard title="Other sections">
                    <OtherSectionsEditor
                      items={resume!.otherSections}
                      onChange={(otherSections) => updateResume({ otherSections })}
                    />
                  </SectionCard>
                  {resume!.unmappedContent.length > 0 && (
                    <SectionCard title="Unmapped content" lowConfidence>
                      <p className="text-[13px] text-text-2">
                        The AI couldn&rsquo;t place this source text. Move it into a section, or
                        remove it deliberately — nothing is dropped silently.
                      </p>
                      <StringListEditor
                        items={resume!.unmappedContent}
                        onChange={(unmappedContent) => updateResume({ unmappedContent })}
                        multiline
                      />
                    </SectionCard>
                  )}
                </>
              )}
              </div>
            </Card>

            <div className="flex min-w-0 flex-col gap-4 self-start xl:sticky xl:top-4">
              <Card>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-text">Live preview</p>
                    <p className="truncate text-[12px] text-text-2">
                      Click any text to edit it here · {sources.length > 1 ? `${sources.length} files` : job.sourceFilename}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center rounded-md border border-border">
                      <button
                        type="button"
                        aria-label="Undo"
                        title="Undo (⌘Z)"
                        disabled={!history.canUndo}
                        onClick={undo}
                        className="px-1.5 py-1.5 text-text-2 hover:text-text disabled:opacity-40"
                      >
                        <Undo2 size={14} aria-hidden />
                      </button>
                      <button
                        type="button"
                        aria-label="Redo"
                        title="Redo (⇧⌘Z)"
                        disabled={!history.canRedo}
                        onClick={redo}
                        className="px-1.5 py-1.5 text-text-2 hover:text-text disabled:opacity-40"
                      >
                        <Redo2 size={14} aria-hidden />
                      </button>
                    </div>
                    {sources.length <= 1 && (
                      <Button
                        asChild
                        variant="secondary"
                        size="sm"
                        disabled={!sources[0]}
                        className={cn(!sources[0] && "pointer-events-none opacity-50")}
                      >
                        <a href={sources[0]?.url} target="_blank" rel="noreferrer">
                          <ExternalLink size={13} strokeWidth={1.75} aria-hidden />
                          Open source
                        </a>
                      </Button>
                    )}
                    <div className="flex items-center rounded-md border border-border">
                      <button
                        type="button"
                        aria-label="Zoom out"
                        disabled={zoom <= 0.5}
                        onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                        className="px-1.5 py-1.5 text-text-2 hover:text-text disabled:opacity-40"
                      >
                        <Minus size={14} aria-hidden />
                      </button>
                      <span className="w-11 text-center text-[12px] tabular-nums text-text-2">
                        {Math.round(zoom * 100)}%
                      </span>
                      <button
                        type="button"
                        aria-label="Zoom in"
                        disabled={zoom >= 2}
                        onClick={() => setZoom((z) => Math.min(2, z + 0.25))}
                        className="px-1.5 py-1.5 text-text-2 hover:text-text disabled:opacity-40"
                      >
                        <Plus size={14} aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>
                {sources.length > 0 && (
                  <div className="flex flex-col gap-1.5 border-b border-border px-4 py-2.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-text-2">
                      Source files
                    </p>
                    {sources.map((s) => (
                      <div
                        key={s.id}
                        className="flex flex-wrap items-center justify-between gap-2 text-[13px]"
                      >
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex min-w-0 items-center gap-1.5"
                        >
                          <ExternalLink size={13} strokeWidth={1.75} aria-hidden />
                          <span className="truncate">{s.filename}</span>
                        </a>
                        {s.editableText && s.appendToOutput && (
                          <div className="flex items-center rounded-md border border-border text-[11px]" data-view-toggle={s.id}>
                            {(["text", "pages"] as const).map((v) => (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setViews((prev) => ({ ...prev, [s.id]: v }))}
                                className={cn(
                                  "px-2 py-0.5",
                                  viewOf(s) === v ? "bg-surface-2 font-semibold text-text" : "text-text-2 hover:text-text",
                                )}
                                title={v === "text" ? "Edit the original's text" : "See the pages as they will print, paint boxes"}
                              >
                                {v === "text" ? "Text" : "Pages"}
                              </button>
                            ))}
                          </div>
                        )}
                        <label
                          className={cn(
                            "flex items-center gap-1.5 whitespace-nowrap text-text-2",
                            !s.canAppend && "opacity-50",
                          )}
                          title={
                            s.canAppend
                              ? "Attach this original file, as-is, after the generated report"
                              : "This file cannot be attached on this server"
                          }
                        >
                          <input
                            type="checkbox"
                            checked={s.appendToOutput}
                            disabled={!s.canAppend}
                            onChange={(e) => void setAppend(s, e.target.checked)}
                          />
                          Append original
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Label className="mb-0 whitespace-nowrap">Template</Label>
                    <Select
                      value={selectedTemplate?.id ?? ""}
                      onChange={(e) => setTemplateId(e.target.value)}
                      className="h-8 w-auto min-w-40"
                      aria-label="Template"
                    >
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} (v{t.version})
                        </option>
                      ))}
                    </Select>
                  </div>
                  <label className="flex items-center gap-1.5 text-[13px] text-text">
                    <input
                      type="checkbox"
                      checked={includeContact}
                      onChange={(e) => setIncludeContact(e.target.checked)}
                    />
                    Contact details
                  </label>
                </div>
                <div className="bg-surface-2 p-3">
                  {selectedTemplate && previewDefinition ? (
                    <TemplatePreview
                      definition={previewDefinition}
                      logoUrl={selectedTemplate.logoUrl}
                      watermarkUrl={selectedTemplate.watermarkUrl}
                      includeContact={includeContact}
                      resume={resume!}
                      photoUrl={job.photoUrl}
                      zoom={zoom}
                      appendix={appendix}
                      edit={{ onChange: commitResume, onUndo: undo, onRedo: redo, onJump: jumpTo }}
                    />
                  ) : (
                    <Skeleton className="h-96 w-full rounded-md" />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
